import React from 'react';
import { Task } from '../../../types/task';
import { GroupBase } from './GroupBase';
import { Box, Text, Flex, HStack, useColorModeValue } from '@chakra-ui/react';
import { FiCalendar } from 'react-icons/fi';
import { Project } from '../../../types/project';
import { TaskItemBase } from './TaskItemBase';
import { addDays, startOfDay, endOfDay, format } from 'date-fns';
import { useTranslation } from 'react-i18next';

// 增强版TaskItem，用于在时间分组中显示项目名称
export const TimeGroupTaskItem: React.FC<{
  task: Task;
  project?: Project;
  onSelect: (task: Task) => void;
  onToggleDone: (task: Task, done: boolean) => void;
  onDelete: (id: string) => void;
}> = ({ task, project, onSelect, onToggleDone, onDelete }) => {
  return (
    <TaskItemBase
      task={task}
      onSelect={onSelect}
      onToggleDone={onToggleDone}
      onDelete={onDelete}
      options={{
        showProjectName: true,
        projectsMap: project ? { [project.id]: project } : {}
      }}
    />
  );
};

export type TimeGroupKey = 'noDate' | 'future' | 'tomorrow' | 'today' | 'yesterday' | 'past';

export const getTimeGroupTitle = (key: TimeGroupKey, t: any): string => {
  return t(`timeGroups.${key}`);
};

export const getDefaultGroupOpenState = (key: TimeGroupKey): boolean => {
  switch (key) {
    case 'noDate': return false;
    case 'future': return false;
    case 'tomorrow': return true;
    case 'today': return true;
    case 'yesterday': return true;
    case 'past': return false;
    default: return false;
  }
};

// 根据时间组获取开始时间
const getStartAtForGroup = (groupKey: TimeGroupKey): string | undefined => {
  const today = new Date();
  switch (groupKey) {
    case 'today':
      return startOfDay(today).toISOString();
    case 'tomorrow':
      return startOfDay(addDays(today, 1)).toISOString();
    case 'yesterday':
      return startOfDay(addDays(today, -1)).toISOString();
    default:
      return undefined;
  }
};

// --- TimeGroup Component Definition ---
export const TimeGroup = ({ 
  groupKey,
  tasks,
  isOpen, 
  onToggle, 
  onSelectTask, 
  onToggleDone, 
  onDeleteTask,
  onQuickAddTask,
  projectsMap
}: { 
  groupKey: TimeGroupKey; 
  tasks: Task[];
  isOpen: boolean;
  onToggle: () => void;
  onSelectTask: (task: Task) => void;
  onToggleDone: (task: Task, done: boolean) => void;
  onDeleteTask: (id: string) => void;
  onQuickAddTask: (projectId: string | null, title: string, startAt?: string) => void;
  projectsMap: Record<string, Project>;
}) => {
  const { t } = useTranslation(['v0/common']);
  
  // 自定义任务渲染器，为每个任务添加项目名称
  const renderTaskWithProject = (
    task: Task,
    onSelect: (task: Task) => void,
    onToggleDone: (task: Task, done: boolean) => void,
    onDelete: (id: string) => void
  ) => (
    <TimeGroupTaskItem
      key={task.id}
      task={task}
      onSelect={onSelect}
      onToggleDone={onToggleDone}
      onDelete={onDelete}
      project={task.projectId ? projectsMap[task.projectId] : undefined}
    />
  );

  // 根据时间组确定是否显示添加按钮
  const shouldShowAddButton = ['today', 'tomorrow', 'yesterday'].includes(groupKey);

  // 处理任务快速添加，合并 GroupBase 的选项和 TimeGroup 的默认 startAt
  const handleTimeGroupQuickAddTask = (title: string, options?: { projectId?: string | null, startAt?: string }) => {
    const defaultStartAt = getStartAtForGroup(groupKey);
    // Use projectId from options if provided, otherwise default to null
    const finalProjectId = options?.projectId ?? null; 
    // Use startAt from GroupBase options if provided, otherwise use the group's default
    const finalStartAt = options?.startAt ?? defaultStartAt; 
    
    onQuickAddTask(finalProjectId, title, finalStartAt); // Call the original handler passed from parent
  };

  return (
    <GroupBase
      title={getTimeGroupTitle(groupKey, t)}
      tasks={tasks}
      isOpen={isOpen}
      onToggle={onToggle}
      onSelectTask={onSelectTask}
      onToggleDone={onToggleDone}
      onDeleteTask={onDeleteTask}
      // Pass the TimeGroup specific quick add handler to GroupBase
      onQuickAddTask={handleTimeGroupQuickAddTask} 
      showAddButton={shouldShowAddButton}
      customTaskRenderer={renderTaskWithProject}
      groupType="time"
      projects={Object.values(projectsMap)}
    />
  );
}; 